import { useEffect, useState } from 'react';
import { Typography, Space, Tabs } from 'antd';
import './style.css';
import {
	CONTENT_MANAGER_MOCK_DATA,
	FUNCTIONAL_GOAL_IDS,
	PLAN_TYPES,
} from './contentManagerMockData';
import { ImageInputCard } from './ImageInputCard';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import {
	getFunctionalGoalById,
	getPlanByPlanType,
	getPreExistingConditions,
	postFunctionalGoal,
	postPlan,
	postPreExistingConditions,
} from '@stores/settings/setings';
import {
	IFunctionalGoals,
	IPlans,
	IPreExistingConditions,
} from '@stores/interfaces';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const ContentManager = () => {
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();
	const [currentPlans, setPlans] = useState<IPlans[]>(
		CONTENT_MANAGER_MOCK_DATA.plans,
	);
	const [preCondition, setPreCondition] = useState<IPreExistingConditions>(
		CONTENT_MANAGER_MOCK_DATA.preExistingConditions,
	);
	const [plansActiveKey, setPlansActiveKey] = useState<string>('1');
	const [goalsActiveKey, setGoalsActiveKey] = useState<string>('1');
	const [goals, setGoals] = useState<IFunctionalGoals[]>();
	const { preExistingConditions, plans, functionalGoals } = useTypedSelector(
		state => state.settings.settings,
	);

	useEffect(() => {
		dispatch(getPreExistingConditions());
		fetchPlans();
		fetchFunctionalGoals();
	}, []);

	useEffect(() => {
    const hasValidPlans = plans.every(plan => plan?.id);
    const sourcePlans = hasValidPlans ? plans : [];
    const mockPlans = CONTENT_MANAGER_MOCK_DATA.plans || [];

    const finalPlans = [];

    for (let i = 0; i < 3; i++) {
      const plan = sourcePlans[i] || {};
      const mock = mockPlans[i] || {};

      finalPlans.push({
        ...mock,
        ...plan,
        title: plan.title || mock.title,
        description: plan.description || mock.description,
        thumbnail: plan.thumbnail || mock.thumbnail || '',
        planType: plan.planType || '',
      });
    }

    setPlans(finalPlans);
  }, [plans]);

	useEffect(() => {
		const hasValidGoals = functionalGoals.every(goal => goal?.id);
    const sourcegoals = hasValidGoals ? functionalGoals : [];
    const mockgoals = CONTENT_MANAGER_MOCK_DATA.goals || [];

    const finalGoals: IFunctionalGoals[] = [];

    for (let i = 0; i < 5; i++) {
      const goal = sourcegoals[i] || {};
      const mock = mockgoals[i] || {};

      finalGoals.push({
        ...mock,
        ...goal,
        title: goal.title || mock.title,
        description: goal.description || mock.description,
        thumbnail: goal.thumbnail || mock.thumbnail || '',
				functionalGoalId: goal?.functionalGoalId,
      });
    }

    setGoals(finalGoals);
	}, [functionalGoals]);

	useEffect(() => {
		if (preExistingConditions?.id) {
			setPreCondition({
				...preCondition,
				title: preExistingConditions?.title
					? preExistingConditions?.title
					: CONTENT_MANAGER_MOCK_DATA.preExistingConditions?.title,
				description: preExistingConditions?.description
					? preExistingConditions?.description
					: CONTENT_MANAGER_MOCK_DATA.preExistingConditions?.description,
				thumbnail: preExistingConditions?.thumbnail
					? preExistingConditions?.thumbnail
					: CONTENT_MANAGER_MOCK_DATA.preExistingConditions?.thumbnail,
			});
		}
	}, [preExistingConditions]);

	const fetchFunctionalGoals = async () => {
		for (const id of FUNCTIONAL_GOAL_IDS) {
			await dispatch(getFunctionalGoalById(id));
		}
	};

	const fetchPlans = async () => {
		for (const planType of PLAN_TYPES) {
			await dispatch(getPlanByPlanType(planType));
		}
	};

	const updatePreExistingConditions = async () => {
		const formData = new FormData();

		formData.append('title', preCondition?.title!);
		formData.append('description', preCondition?.description!);
		preCondition?.imageFile
			? formData.append('thumbnail', preCondition?.imageFile!)
			: null;

		await dispatch(postPreExistingConditions(formData));
	};

	const updatePlans = async () => {
		const formData = new FormData();

		const index = parseInt(plansActiveKey) - 1;

		formData.append('planType', PLAN_TYPES[index]);
		formData.append('title', currentPlans![index]?.title!);
		formData.append('description', currentPlans![index]?.description!);
		currentPlans![index]?.imageFile
			? formData.append('thumbnail', currentPlans![index]?.imageFile!)
			: null;

		await dispatch(postPlan(formData));
	};

	const updateGoals = async () => {
		const formData = new FormData();

		const index = parseInt(goalsActiveKey) - 1;

		formData.append('functionalGoalId', FUNCTIONAL_GOAL_IDS[index].toString());
		formData.append('title', goals![index]?.title!);
		formData.append('description', goals![index]?.description!);
		goals![index]?.imageFile
			? formData.append('thumbnail', goals![index]?.imageFile!)
			: null;

		await dispatch(postFunctionalGoal(formData));
	};

	return (
		<div style={{ marginTop: 40 }}>
			<Space direction="vertical" size={32} style={{ width: '100%' }}>
				<div>
					<Title style={{color: 'var(--content-manager-card-header-text-color)'}} level={3}>{t('Admin.data.menu.setting.plan1')}</Title>
					<div className="content-manager-card">
						<div className="content-manager-card-header">
							<h2 className="content-manager-card-title">
								{t('Admin.data.menu.setting.plan1description')}
							</h2>
							<button
								className="content-manager-update-button"
								onClick={() => updatePlans()}>
								{t('Admin.data.menu.setting.update')}
							</button>
						</div>
						<div className="content-manager-card-body">
							<Tabs
								defaultActiveKey={plansActiveKey}
								activeKey={plansActiveKey}
								tabPosition="left"
								className="content-manager-tabs"
								onChange={value => setPlansActiveKey(value)}
								items={currentPlans?.map((plan, idx) => {
									return {
										label: (
											<span className="eclipse-text">
												{plans[idx]?.title ||
													CONTENT_MANAGER_MOCK_DATA?.plans[idx]?.title}
											</span>
										),
										key: `${idx + 1}`,
										children: (
											<ImageInputCard
												key={idx}
												data={plan || []}
												onChange={(newData: IPlans) => {
													const updated = [...currentPlans];
													updated[idx] = newData;
													setPlans(updated);
												}}
											/>
										),
									};
								})}
							/>
						</div>
					</div>
				</div>
				<div>
					<Title style={{color: 'var(--content-manager-card-header-text-color)'}} level={3}>{t('Admin.data.menu.setting.plan2')}</Title>
					<div className="content-manager-card">
						<div className="content-manager-card-header">
							<h2 className="content-manager-card-title">
								{t('Admin.data.menu.setting.plan2description')}
							</h2>
							<button
								className="content-manager-update-button"
								onClick={updatePreExistingConditions}>
								{t('Admin.data.menu.setting.update')}
							</button>
						</div>
						<div className="content-manager-card-body">
							{preCondition && (
								<ImageInputCard
									key="preCondition"
									data={
										preCondition ||
										CONTENT_MANAGER_MOCK_DATA?.preExistingConditions
									}
									onChange={setPreCondition}
								/>
							)}
						</div>
					</div>
				</div>
				<div>
					<Title style={{color: 'var(--content-manager-card-header-text-color)'}} level={3}>{t('Admin.data.menu.setting.plan3')}</Title>
					<div className="content-manager-card">
						<div className="content-manager-card-header">
							<h2 className="content-manager-card-title">
								{t('Admin.data.menu.setting.plan3description')}
							</h2>
							<button
								className="content-manager-update-button"
								onClick={() => updateGoals()}>
								{t('Admin.data.menu.setting.update')}
							</button>
						</div>
						<div className="content-manager-card-body">
							<Tabs
								defaultActiveKey={goalsActiveKey}
								activeKey={goalsActiveKey}
								tabPosition="left"
								className="content-manager-tabs"
								onChange={value => setGoalsActiveKey(value)}
								items={goals?.map((goal, idx) => {
									return {
										label: (
											<span className="eclipse-text">
												{functionalGoals[idx]?.title ||
													CONTENT_MANAGER_MOCK_DATA.goals[idx]?.title}
											</span>
										),
										key: `${idx + 1}`,
										children: (
											<ImageInputCard
												key={idx}
												data={goal || []}
												onChange={(newData: IFunctionalGoals) => {
													const updated = [...goals];
													updated[idx] = newData;
													setGoals(updated);
												}}
											/>
										),
									};
								})}
							/>
						</div>
					</div>
				</div>
			</Space>
		</div>
	);
};

export default ContentManager;
